import { useMemo } from 'react';
import { PermissionVerb, StaticResource } from '@shared/utils/src/types';
import { useSession } from 'next-auth/react';
import type { Session } from 'next-auth';
import { UI_MESSAGES } from '@shared/constants';

/**
 * Hook to get the current client session data and status.
 * 
 * @returns An object containing the current session data and status
 */
/**
 * @internal
 */
function useClientSession(): { session: Session | null; status: string } {
  const result = useSession();
  const session: Session | null = result?.data ?? null;
  const status: string = result?.status ?? UI_MESSAGES.STATUS.LOADING;
  return { session, status };
}

/**
 * Hook for client-side permission checks
 * @param resource The resource to check permissions for
 * @param verbs The verbs to check permissions for
 * @param context Optional context for permission checking
 * @returns Object containing hasPermission boolean
 */
export function useAuthorize(
  resource: StaticResource,
  verbs: PermissionVerb[],
  context?: {
    targetUserId?: string;
    targetTenantId?: string;
  }
) {

  const { session } = useClientSession();
  
  const hasPermission = useMemo(() => {
    if (!session?.user?.permissions) return false;
    
    // Check if user has the permissions
    const resourcePermissions = session.user.permissions[resource] || [];
    const hasAllVerbs = verbs.every(verb => resourcePermissions.includes(verb));
    
    if (!hasAllVerbs) return false;
    
    // Check tenant access if context provided
    if (context?.targetTenantId && context.targetTenantId !== session.user.tenantId) {
      return false;
    }
    
    // Check user access if context provided
    if (context?.targetUserId && context.targetUserId !== session.user.id) {
      return false;
    }
    
    return true;
  }, [session, resource, verbs, context]);
  
  return { hasPermission };
}

// Export the useClientSession hook
export { useClientSession }; 